import { BoltIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { TFunction } from 'i18next';
import { ConditionalStyles, TableColumn } from 'react-data-table-component';
import { StatusModel, ReceptionCategory, ElevatorStatus } from 'src/api/eris/models/StatusModel';

export const resolveReceptionIcon = (category: ReceptionCategory) => {
  switch (category) {
    case ReceptionCategory.helios:
      return (
        <span key="helios">
          <BoltIcon className="size-9 bg-black p-1 text-yellow-400" />
        </span>
      );
    case ReceptionCategory.customerContact:
      return (
        <span key="contact">
          <PhoneIcon className="size-9 bg-black p-1 text-white" />
        </span>
      );
    default:
      return null;
  }
};

export const resolveRowStyle = (status: ElevatorStatus) => {
  switch (status) {
    case ElevatorStatus.recoveryComplete:
      return { backgroundColor: '#bae7ff' };
    case ElevatorStatus.suspended:
      return { backgroundColor: '#ffe58f' };
    case ElevatorStatus.communicationInProgress:
      return { backgroundColor: '#ffffff' };
    case ElevatorStatus.noStatus:
      return { backgroundColor: '#f0f0f0' };
    default:
      return {};
  }
};

export const resolveSupportStatus = (status: number, t: TFunction) => {
  switch (status) {
    case 1:
      return t('eris.supportStatusList.restored');
    case 2:
      return t('eris.supportStatusList.autoRestored');
    case 3:
      return t('eris.supportStatusList.accessDenied');
    case 4:
      return t('eris.supportStatusList.repairNeeded');
    case 5:
      return t('eris.supportStatusList.parking');
    default:
      return status;
  }
};

export const conditionalRowStyles: ConditionalStyles<StatusModel>[] = [
  {
    when: () => true,
    style: (row: StatusModel) => resolveRowStyle(row.operationStatus),
  },
];

export const columns = (
  page: number,
  size: number,
  t: TFunction<'translation', undefined>
): TableColumn<StatusModel>[] => {
  return [
    {
      name: t('eris.supportStatusList.number'),
      cell: (_, index) => (
        <div className="flex size-full min-w-16 items-center justify-center bg-blue-900 text-white">
          {page * size + index + 1}
        </div>
      ),
      width: '4rem',
      sortable: false,
    },
    {
      name: t('eris.supportStatusList.customerNumber'),
      cell: (row) => <div className="px-2">{row.customerNumber}</div>,
      width: '11rem',
      sortable: false,
    },
    {
      name: t('eris.supportStatusList.unit'),
      cell: (row) => <div className="px-2">{row.unit}</div>,
      width: '11rem',
      sortable: false,
    },
    {
      name: t('eris.supportStatusList.buildingName'),
      cell: (row) => <div className="px-2">{row.buildingName}</div>,
      sortable: false,
    },
    {
      name: t('eris.supportStatusList.address'),
      cell: (row) => <div className="px-2">{row.address}</div>,
      sortable: false,
    },
    {
      name: t('eris.supportStatusList.helios'),
      cell: (row) => (
        <span className="flex min-w-24 items-center justify-center">{row.helios ? '○' : ''}</span>
      ),
      width: '6rem',
      sortable: false,
    },
    {
      name: t('eris.supportStatusList.sensor'),
      cell: (row) => (
        <span className="flex min-w-24 items-center justify-center">{row.sensor ? '○' : ''}</span>
      ),
      width: '6rem',
      sortable: false,
    },
    {
      name: t('eris.supportStatusList.reception'),
      cell: (row) => (
        <div className="flex size-full items-center justify-center gap-1">
          {row.receptionIndicator?.map((category: number) => resolveReceptionIcon(category))}
        </div>
      ),
      width: '7rem',
      sortable: false,
    },
    {
      name: t('eris.supportStatusList.entrapment'),
      cell: (row) => (
        <div className="px-2">
          {row.confinementStatus === 3
            ? t('eris.common.entrapment.rescue')
            : row.confinementStatus === 2
              ? t('eris.common.entrapment.closure')
              : ''}
        </div>
      ),
      sortable: false,
    },
    {
      name: t('eris.supportStatusList.supportStatus'),
      cell: (row, index) => (
        <div className="px-2" data-testid={`support-status-cell-${index}`}>
          {row.supportStatus != null ? resolveSupportStatus(Number(row.supportStatus), t) : ''}
        </div>
      ),
      sortable: false,
    },
  ];
};
